import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';
import PayrollTabs from '../../components/PayrollTabs';
import { downloadBlob } from '../../utils/downloadFile';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700',
  finalized: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
};

// One attendance row per payroll employee, editable inline, saved
// individually via POST /payroll/attendance (upsert) - separate from the
// run's own PayrollLine, since attendance can be entered before a run
// even exists. `profile` here is a PayrollProfile (its own independent
// person record - see PayrollEmployeesPage's docstring), not an Employee.
const AttendanceRow = ({ profile, attendance, prevPendingLeave, month, year, onSaved }) => {
  const [form, setForm] = useState({
    // Present days are typed directly out of a fixed 30-day month (an
    // absent day = one less than 30), so there is no Month Days field.
    present_days: attendance?.present_days ?? '',
    late_count: attendance?.late_count ?? '',
    // Pending leave (days) replaced the old Hours Worked column
    // (2026-11-04) and prints on the payslip. A month with nothing saved
    // yet starts from LAST month's figure; HR edits it when it changed.
    pending_leave: attendance?.pending_leave ?? prevPendingLeave ?? '',
  });
  const [saving, setSaving] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const save = async () => {
    if (form.present_days === '') {
      showToast('Present days are required', 'error');
      return;
    }
    if (Number(form.present_days) < 0 || Number(form.present_days) > 30) {
      showToast('Present days must be between 0 and 30', 'error');
      return;
    }
    setSaving(true);
    try {
      await payrollService.upsertAttendance({
        payroll_employee_id: profile.id,
        month, year,
        present_days: Number(form.present_days),
        month_days: 30,
        late_count: form.late_count === '' ? null : Number(form.late_count),
        pending_leave: form.pending_leave === '' ? null : Number(form.pending_leave),
      });
      showToast(`Attendance saved for ${profile.name}`, 'success');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save attendance', 'error');
    }
    setSaving(false);
  };

  return (
    <tr className="border-b border-gray-50 last:border-0">
      <td className="px-4 py-2">{profile.employee_code || '—'}</td>
      <td className="px-4 py-2 font-medium text-gray-900">{profile.name}</td>
      <td className="px-4 py-2"><input type="number" step="0.5" min="0" max="30" value={form.present_days} onChange={(e) => setForm({ ...form, present_days: e.target.value })} className="w-16 border border-gray-300 rounded px-2 py-1 text-sm" /></td>
      <td className="px-4 py-2"><input type="number" value={form.late_count} onChange={(e) => setForm({ ...form, late_count: e.target.value })} placeholder="—" className="w-16 border border-gray-300 rounded px-2 py-1 text-sm" /></td>
      <td className="px-4 py-2"><input type="number" step="0.5" value={form.pending_leave} onChange={(e) => setForm({ ...form, pending_leave: e.target.value })} placeholder="—" title={attendance?.pending_leave == null && prevPendingLeave != null ? 'Carried over from last month - edit if it changed' : undefined} className="w-20 border border-gray-300 rounded px-2 py-1 text-sm" /></td>
      <td className="px-4 py-2">
        <button onClick={save} disabled={saving} className="text-komal-blue hover:underline text-xs font-semibold disabled:opacity-60">
          {saving ? 'Saving...' : attendance ? 'Update' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

const LineRow = ({ line, runId, editable, selected, onToggleSelect, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ income_tax_deduction: line.income_tax_deduction, other_deduction: line.other_deduction, notes: line.notes || '' });
  const [saving, setSaving] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const save = async () => {
    setSaving(true);
    try {
      await payrollService.updateLine(runId, line.id, {
        income_tax_deduction: Number(form.income_tax_deduction) || 0,
        other_deduction: Number(form.other_deduction) || 0,
        notes: form.notes,
      });
      showToast('Payslip line updated', 'success');
      setEditing(false);
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update line', 'error');
    }
    setSaving(false);
  };

  return (
    <tr className="border-b border-gray-50 last:border-0">
      <td className="px-4 py-2"><input type="checkbox" checked={selected} onChange={() => onToggleSelect(line.payroll_employee_id)} /></td>
      <td className="px-4 py-2 text-gray-500">{line.employee_code || '—'}</td>
      <td className="px-4 py-2 font-medium text-gray-900">{line.employee_name}</td>
      <td className="px-4 py-2 text-right">{line.present_days}/{line.month_days}</td>
      <td className="px-4 py-2 text-right">{money(line.earned_gross)}</td>
      <td className="px-4 py-2 text-right">{money(line.pf_deduction)}</td>
      <td className="px-4 py-2 text-right">{money(line.pt_deduction)}</td>
      <td className="px-4 py-2 text-right">{money(line.loan_deduction)}</td>
      <td className="px-4 py-2 text-right">
        {editing ? (
          <input type="number" value={form.income_tax_deduction} onChange={(e) => setForm({ ...form, income_tax_deduction: e.target.value })} className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right" />
        ) : money(line.income_tax_deduction)}
      </td>
      <td className="px-4 py-2 text-right">
        {editing ? (
          <input type="number" value={form.other_deduction} onChange={(e) => setForm({ ...form, other_deduction: e.target.value })} className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right" />
        ) : money(line.other_deduction)}
      </td>
      <td className="px-4 py-2 text-right font-semibold">{money(line.total_deductions)}</td>
      <td className="px-4 py-2 text-right font-bold text-gray-900">{money(line.net_payable)}</td>
      <td className="px-4 py-2 whitespace-nowrap">
        {editable && (
          editing ? (
            <span className="space-x-2">
              <button onClick={save} disabled={saving} className="text-green-700 hover:underline text-xs font-semibold">{saving ? 'Saving...' : 'Save'}</button>
              <button onClick={() => setEditing(false)} className="text-gray-500 hover:underline text-xs font-semibold">Cancel</button>
            </span>
          ) : (
            <button onClick={() => setEditing(true)} className="text-komal-blue hover:underline text-xs font-semibold">Edit</button>
          )
        )}
      </td>
    </tr>
  );
};

const PayrollRunDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [attendanceByEmployee, setAttendanceByEmployee] = useState({});
  const [prevPendingLeaveByEmployee, setPrevPendingLeaveByEmployee] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [draftingEmails, setDraftingEmails] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      payrollService.getRun(id),
      payrollService.getProfiles({ is_active: true }),
    ])
      .then(async ([runRes, profRes]) => {
        const runData = runRes.data.run;
        setRun(runData);
        const scopedProfiles = runData.branch ? profRes.data.profiles.filter((p) => p.branch === runData.branch) : profRes.data.profiles;
        setProfiles(scopedProfiles);
        const attRes = await payrollService.getAttendance({ month: runData.month, year: runData.year });
        const byEmp = {};
        (attRes.data.attendance || []).forEach((a) => { byEmp[a.payroll_employee_id] = a; });
        setAttendanceByEmployee(byEmp);
        // Last month's pending leave, to start this month's column from.
        // A failure here (or no previous month) just leaves the column
        // empty - it must never block loading the run itself.
        const prevMonth = runData.month === 1 ? 12 : runData.month - 1;
        const prevYear = runData.month === 1 ? runData.year - 1 : runData.year;
        const prevByEmp = {};
        try {
          const prevRes = await payrollService.getAttendance({ month: prevMonth, year: prevYear });
          (prevRes.data.attendance || []).forEach((a) => {
            if (a.pending_leave != null) prevByEmp[a.payroll_employee_id] = a.pending_leave;
          });
        } catch (e) { /* leave empty */ }
        setPrevPendingLeaveByEmployee(prevByEmp);
        // Default selection = every calculated line, so "Generate Payslips"
        // works with zero clicks for the common case (everyone).
        setSelectedIds(new Set(runData.lines.map((l) => l.payroll_employee_id)));
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load payroll run', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(load, [load]);

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await payrollService.calculateRun(id);
      showToast(`Calculated ${res.data.calculated_count} payslip(s)${res.data.skipped.length ? `, ${res.data.skipped.length} skipped (no attendance yet)` : ''}`, 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to calculate run', 'error');
    }
    setCalculating(false);
  };

  const handleFinalize = async () => {
    if (!window.confirm('Finalize this payroll run? Attendance and payslip lines can no longer be edited or recalculated after this.')) return;
    setFinalizing(true);
    try {
      await payrollService.finalizeRun(id);
      showToast('Payroll run finalized', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to finalize run', 'error');
    }
    setFinalizing(false);
  };

  const handleMarkPaid = async () => {
    if (!window.confirm('Mark this payroll run as paid?')) return;
    setMarking(true);
    try {
      await payrollService.markRunPaid(id);
      showToast('Payroll run marked paid', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to mark run paid', 'error');
    }
    setMarking(false);
  };

  const toggleSelect = (payrollEmployeeId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(payrollEmployeeId)) next.delete(payrollEmployeeId); else next.add(payrollEmployeeId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === run.lines.length ? new Set() : new Set(run.lines.map((l) => l.payroll_employee_id))
    );
  };

  const handleGeneratePayslips = async () => {
    if (selectedIds.size === 0) {
      showToast('Select at least one employee first', 'error');
      return;
    }
    setGeneratingPdf(true);
    try {
      const res = await payrollService.downloadPayslipsPdf(id, [...selectedIds]);
      downloadBlob(res.data, `payslips_${MONTHS[run.month]}_${run.year}.pdf`);
    } catch (err) {
      showToast('Failed to generate payslips PDF', 'error');
    }
    setGeneratingPdf(false);
  };

  const handleDraftPayslips = async () => {
    if (selectedIds.size === 0) {
      showToast('Select at least one employee first', 'error');
      return;
    }
    if (!window.confirm(`Save payslip drafts for ${selectedIds.size} selected employee(s) to your mailbox's Drafts folder? Nothing is sent automatically - you'll review and send each one yourself.`)) return;
    setDraftingEmails(true);
    try {
      const res = await payrollService.draftPayslipsEmail(id, [...selectedIds]);
      const { drafted_count, failed_count, skipped_count } = res.data;
      const parts = [`${drafted_count} drafted`];
      if (failed_count) parts.push(`${failed_count} failed`);
      if (skipped_count) parts.push(`${skipped_count} skipped (no email on file)`);
      showToast(parts.join(', '), failed_count || skipped_count ? 'info' : 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save payslip drafts', 'error');
    }
    setDraftingEmails(false);
  };

  if (loading || !run) {
    return <div className="min-h-screen p-6"><p className="text-white/80 text-sm">Loading...</p></div>;
  }

  const isDraft = run.status === 'draft';
  const totals = run.lines.reduce((acc, l) => ({ gross: acc.gross + l.earned_gross, net: acc.net + l.net_payable }), { gross: 0, net: 0 });

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <PayrollTabs />

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <button onClick={() => navigate('/payroll/runs')} className="text-xs text-white/70 hover:text-white mb-1">← All Runs</button>
            <h1 className="text-xl font-bold text-white flex items-center gap-3">
              {MONTHS[run.month]} {run.year} {run.branch ? `— ${run.branch}` : '— All Branches'}
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_STYLE[run.status]}`}>{run.status}</span>
            </h1>
          </div>
          <div className="flex gap-2">
            {isDraft && (
              <button onClick={handleCalculate} disabled={calculating} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                {calculating ? 'Calculating...' : run.lines.length ? 'Recalculate' : 'Calculate Payslips'}
              </button>
            )}
            {run.lines.length > 0 && (
              <button onClick={handleGeneratePayslips} disabled={generatingPdf} className="px-4 py-2 text-sm rounded border border-white/40 text-white hover:bg-white/10 font-semibold disabled:opacity-60">
                {generatingPdf ? 'Generating...' : `Generate Payslips PDF (${selectedIds.size})`}
              </button>
            )}
            {run.lines.length > 0 && (
              <button onClick={handleDraftPayslips} disabled={draftingEmails} className="px-4 py-2 text-sm rounded border border-white/40 text-white hover:bg-white/10 font-semibold disabled:opacity-60">
                {draftingEmails ? 'Saving drafts...' : `Save Payslip Drafts (${selectedIds.size})`}
              </button>
            )}
            {isDraft && run.lines.length > 0 && (
              <button onClick={handleFinalize} disabled={finalizing} className="px-4 py-2 text-sm rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-60">
                {finalizing ? 'Finalizing...' : 'Finalize'}
              </button>
            )}
            {run.status === 'finalized' && (
              <button onClick={handleMarkPaid} disabled={marking} className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60">
                {marking ? 'Marking...' : 'Mark Paid'}
              </button>
            )}
          </div>
        </div>

        {isDraft && (
          <div className="glass-card mb-6">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Attendance for {MONTHS[run.month]} {run.year}</h2>
              <p className="text-xs text-gray-400 mt-1">Enter each employee's present days out of 30 (one day absent = 29), then Calculate Payslips above. Pending Leave starts from last month's figure - change it if it moved. Or use "Load Old Salary Sheet" from the Payroll Runs page to fill this from a workbook.</p>
            </div>
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Code</th>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Present (of 30)</th>
                    <th className="px-4 py-2 font-medium">Late Days</th>
                    <th className="px-4 py-2 font-medium">Pending Leave</th>
                    <th className="px-4 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <AttendanceRow
                      key={p.id}
                      profile={p}
                      attendance={attendanceByEmployee[p.id]}
                      prevPendingLeave={prevPendingLeaveByEmployee[p.id]}
                      month={run.month}
                      year={run.year}
                      onSaved={load}
                    />
                  ))}
                  {profiles.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No active employees in this branch's Employee Master.</td></tr>
                  )}
                </tbody>
              </table>
            </ScrollX>
          </div>
        )}

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Payslips ({run.lines.length})</h2>
            {run.lines.length > 0 && (
              <div className="text-sm text-gray-600">
                Total Gross: <b>{money(totals.gross)}</b> &nbsp;·&nbsp; Total Net: <b>{money(totals.net)}</b>
              </div>
            )}
          </div>
          <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-medium">
                    {run.lines.length > 0 && (
                      <input type="checkbox" checked={selectedIds.size === run.lines.length} onChange={toggleSelectAll} title="Select all" />
                    )}
                  </th>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium text-right">Present</th>
                  <th className="px-4 py-2 font-medium text-right">Gross</th>
                  <th className="px-4 py-2 font-medium text-right">PF</th>
                  <th className="px-4 py-2 font-medium text-right">PT</th>
                  <th className="px-4 py-2 font-medium text-right">Loan</th>
                  <th className="px-4 py-2 font-medium text-right">Income Tax</th>
                  <th className="px-4 py-2 font-medium text-right">Other</th>
                  <th className="px-4 py-2 font-medium text-right">Total Ded.</th>
                  <th className="px-4 py-2 font-medium text-right">Net Payable</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {run.lines.map((l) => (
                  <LineRow
                    key={l.id} line={l} runId={run.id} editable={isDraft}
                    selected={selectedIds.has(l.payroll_employee_id)}
                    onToggleSelect={toggleSelect}
                    onSaved={load}
                  />
                ))}
                {run.lines.length === 0 && (
                  <tr><td colSpan={13} className="px-4 py-6 text-center text-gray-400">Not calculated yet - enter attendance above, then Calculate Payslips.</td></tr>
                )}
              </tbody>
            </table>
          </ScrollX>
        </div>
      </div>
    </div>
  );
};

export default PayrollRunDetailPage;
